(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-pdp-social-showcase]';
  var initializedAttribute = 'data-pdp-social-initialized';

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function initializeSection(root) {
    if (!root || root.hasAttribute(initializedAttribute)) return;
    root.setAttribute(initializedAttribute, 'true');

    var cards = toArray(root.querySelectorAll('[data-social-card]'));
    var modal = root.querySelector('[data-social-modal]');
    var viewport = root.querySelector('[data-social-viewport]');
    var previousButton = root.querySelector('[data-social-previous]');
    var nextButton = root.querySelector('[data-social-next]');
    var dots = toArray(root.querySelectorAll('[data-social-dot]'));
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var activeIndex = 0;
    var lastFocusedElement = null;
    var cardObserver = null;
    var touchStartX = 0;
    var touchStartY = 0;

    function loadCardVideo(video) {
      if (!video || video.dataset.loaded === 'true') return;
      toArray(video.querySelectorAll('source[data-src]')).forEach(function (source) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
      video.dataset.loaded = 'true';
    }

    function pauseCardVideos() {
      cards.forEach(function (card) {
        var video = card.querySelector('[data-card-video]');
        if (video) video.pause();
      });
    }

    function initializeCardVideos() {
      var videos = toArray(root.querySelectorAll('[data-card-video]'));
      if (!videos.length) return;

      if (!('IntersectionObserver' in window)) {
        videos.forEach(loadCardVideo);
        return;
      }

      cardObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            loadCardVideo(video);
            if (!reducedMotion && (!modal || modal.hidden)) {
              var playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
            }
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.3, rootMargin: '120px 0px' });

      videos.forEach(function (video) {
        cardObserver.observe(video);
      });
    }

    function getScrollStep() {
      var firstCard = viewport && viewport.querySelector('[data-social-card]');
      if (!firstCard) return 336;
      var track = root.querySelector('[data-social-track]');
      var gap = track ? parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) : 24;
      return firstCard.getBoundingClientRect().width + (Number.isFinite(gap) ? gap : 24);
    }

    function updateCarouselControls() {
      if (!viewport) return;
      var maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      var position = viewport.scrollLeft;
      if (previousButton) previousButton.disabled = position <= 2;
      if (nextButton) nextButton.disabled = position >= maximum - 2;

      if (dots.length) {
        var step = getScrollStep();
        var selected = clamp(Math.round(position / step), 0, dots.length - 1);
        dots.forEach(function (dot, index) {
          dot.setAttribute('aria-current', index === selected ? 'true' : 'false');
        });
      }
    }

    if (viewport) {
      var scrollFrame = null;
      viewport.addEventListener('scroll', function () {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(function () {
          updateCarouselControls();
          scrollFrame = null;
        });
      }, { passive: true });

      if (previousButton) {
        previousButton.addEventListener('click', function () {
          viewport.scrollBy({ left: -getScrollStep(), behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', function () {
          viewport.scrollBy({ left: getScrollStep(), behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          viewport.scrollTo({ left: getScrollStep() * index, behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      });

      updateCarouselControls();
      window.addEventListener('resize', updateCarouselControls, { passive: true });
    }

    if (!modal || !cards.length) {
      initializeCardVideos();
      return;
    }

    var modalPanel = modal.querySelector('[data-social-modal-panel]');
    var modalStage = modal.querySelector('[data-social-modal-stage]');
    var modalImage = modal.querySelector('[data-social-modal-image]');
    var modalVideo = modal.querySelector('[data-social-modal-video]');
    var modalClose = modal.querySelector('[data-social-modal-close]');
    var modalSound = modal.querySelector('[data-social-modal-sound]');
    var modalName = toArray(modal.querySelectorAll('[data-social-modal-name]'));
    var modalHandle = toArray(modal.querySelectorAll('[data-social-modal-handle]'));
    var modalPlatform = toArray(modal.querySelectorAll('[data-social-modal-platform]'));
    var modalMetaDots = toArray(modal.querySelectorAll('.pdp-social-modal__meta-dot'));
    var modalQuote = toArray(modal.querySelectorAll('[data-social-modal-quote]'));
    var modalLink = toArray(modal.querySelectorAll('[data-social-modal-link]'));
    var progress = modal.querySelector('[data-social-progress]');

    function setText(elements, value) {
      elements.forEach(function (element) {
        element.textContent = value || '';
        element.hidden = !value;
      });
    }

    function setLink(elements, href) {
      elements.forEach(function (element) {
        if (href) {
          element.href = href;
          element.hidden = false;
        } else {
          element.removeAttribute('href');
          element.hidden = true;
        }
      });
    }

    function updateProgress(value) {
      if (!progress) return;
      var segments = toArray(progress.children);
      segments.forEach(function (segment, index) {
        var fill = segment.firstElementChild;
        if (!fill) return;
        var amount = index < activeIndex ? 100 : index === activeIndex ? value : 0;
        fill.style.width = amount + '%';
      });
    }

    function buildProgress() {
      if (!progress) return;
      progress.innerHTML = '';
      cards.forEach(function (_, index) {
        var segment = document.createElement('span');
        segment.className = 'pdp-social-modal__progress-segment';
        segment.setAttribute('aria-hidden', 'true');
        var fill = document.createElement('span');
        segment.appendChild(fill);
        progress.appendChild(segment);
      });
      updateProgress(0);
    }

    function updateSoundButton() {
      if (!modalSound || !modalVideo) return;
      var muted = modalVideo.muted;
      modalSound.setAttribute('aria-label', muted ? 'Turn sound on' : 'Mute video');
      modalSound.setAttribute('aria-pressed', muted ? 'false' : 'true');
      modalSound.dataset.muted = muted ? 'true' : 'false';
    }

    function stopModalVideo() {
      if (!modalVideo) return;
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.removeAttribute('poster');
      modalVideo.load();
    }

    function renderActiveCard(index) {
      activeIndex = clamp(index, 0, cards.length - 1);
      var card = cards[activeIndex];
      var isVideo = card.dataset.mediaType === 'video';
      var mediaSource = card.dataset.mediaSrc || '';
      var posterSource = card.dataset.posterSrc || '';
      var mediaAlt = card.dataset.mediaAlt || '';

      setText(modalName, card.dataset.name);
      setText(modalHandle, card.dataset.handle);
      setText(modalPlatform, card.dataset.platform);
      modalMetaDots.forEach(function (dot) {
        dot.hidden = !(card.dataset.handle && card.dataset.platform);
      });
      setText(modalQuote, card.dataset.quote);

      /* Prefer the exact post, fall back to the creator's profile. A video file
         carries no record of the post it came from, so for anything sourced from
         the product's Social videos the profile is all there honestly is. */
      var outboundUrl = card.dataset.postUrl || card.dataset.profileUrl || '';
      setLink(modalLink, outboundUrl);
      modalLink.forEach(function (link) {
        var label = link.querySelector('[data-social-link-label]');
        if (!label) return;
        label.textContent = (!card.dataset.postUrl && card.dataset.profileLabel)
          ? card.dataset.profileLabel
          : (link.dataset.defaultLabel || label.textContent);
      });

      /* Nothing written about this post means there is nothing to read, so the
         panel drops to attribution and the way out to the creator, and the video
         gets the room instead of sitting beside an empty column. */
      var hasWriting = !!(card.dataset.quote && card.dataset.quote.trim());
      modal.classList.toggle('pdp-social-modal--bare', !hasWriting);
      buildProgress();

      if (modalImage) {
        modalImage.alt = mediaAlt;
        modalImage.hidden = isVideo;
        if (!isVideo) modalImage.src = mediaSource;
        else modalImage.removeAttribute('src');
      }

      stopModalVideo();
      if (modalVideo) {
        modalVideo.hidden = !isVideo;
        if (isVideo) {
          modalVideo.src = mediaSource;
          if (posterSource) modalVideo.poster = posterSource;
          modalVideo.load();

          /* Open with sound. The cards themselves run muted, because a page that
             makes noise on its own is the thing everybody hates, but opening one
             is a deliberate click and a click is exactly the gesture browsers
             require before audio is allowed.

             Not every browser agrees, and a rejected play() leaves a black frame
             rather than an error anybody would notice, so a refusal falls back to
             muted playback and puts the sound button back in front of the shopper
             instead of silently showing them a still. */
          modalVideo.muted = false;
          modalVideo.volume = 1;
          var playPromise = modalVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {
              modalVideo.muted = true;
              updateSoundButton();
              var retry = modalVideo.play();
              if (retry && typeof retry.catch === 'function') retry.catch(function () {});
            });
          }
        }
      }

      if (modalSound) modalSound.hidden = !isVideo;
      if (!isVideo) updateProgress(100);
      updateSoundButton();

      modal.setAttribute('aria-label', (card.dataset.name || 'Social post') + ', item ' + (activeIndex + 1) + ' of ' + cards.length);
    }

    function openModal(index, trigger) {
      lastFocusedElement = trigger || document.activeElement;
      pauseCardVideos();
      renderActiveCard(index);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pdp-social-modal-open');
      window.requestAnimationFrame(function () {
        modal.classList.add('is-open');
        if (modalClose) modalClose.focus({ preventScroll: true });
      });
    }

    function closeModal() {
      if (modal.hidden) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      stopModalVideo();
      document.documentElement.classList.remove('pdp-social-modal-open');
      window.setTimeout(function () {
        modal.hidden = true;
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') lastFocusedElement.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 200);
    }

    function showRelativeCard(direction) {
      var nextIndex = (activeIndex + direction + cards.length) % cards.length;
      renderActiveCard(nextIndex);
    }

    cards.forEach(function (card, index) {
      card.addEventListener('click', function () {
        openModal(index, card);
      });
    });

    if (modalClose) modalClose.addEventListener('click', closeModal);
    modal.addEventListener('click', function (event) {
      if (event.target === modal) closeModal();
    });

    if (modalSound) {
      modalSound.addEventListener('click', function (event) {
        event.stopPropagation();
        if (!modalVideo) return;
        modalVideo.muted = !modalVideo.muted;
        updateSoundButton();
      });
    }

    if (modalStage) {
      modalStage.addEventListener('click', function (event) {
        if (!modalVideo || modalVideo.hidden || event.target.closest('button, a')) return;
        if (modalVideo.paused) modalVideo.play().catch(function () {});
        else modalVideo.pause();
      });

      modalStage.addEventListener('touchstart', function (event) {
        if (!event.touches.length) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }, { passive: true });

      modalStage.addEventListener('touchend', function (event) {
        if (!event.changedTouches.length) return;
        var deltaX = event.changedTouches[0].clientX - touchStartX;
        var deltaY = event.changedTouches[0].clientY - touchStartY;
        if (Math.abs(deltaY) > 70 && deltaY > 0 && Math.abs(deltaY) > Math.abs(deltaX)) {
          closeModal();
        } else if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY)) {
          showRelativeCard(deltaX < 0 ? 1 : -1);
        }
      }, { passive: true });
    }

    if (modalVideo) {
      modalVideo.addEventListener('timeupdate', function () {
        if (!modalVideo.duration || !Number.isFinite(modalVideo.duration)) return;
        updateProgress(clamp((modalVideo.currentTime / modalVideo.duration) * 100, 0, 100));
      });
      modalVideo.addEventListener('ended', function () {
        showRelativeCard(1);
      });
    }

    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showRelativeCard(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showRelativeCard(-1);
      } else if (event.key === 'Tab') {
        var focusable = toArray(modal.querySelectorAll('button:not([hidden]), a[href]:not([hidden]), video[controls]'))
          .filter(function (element) { return element.offsetParent !== null; });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    initializeCardVideos();

    root.addEventListener('shopify:section:unload', function () {
      if (cardObserver) cardObserver.disconnect();
      closeModal();
    }, { once: true });
  }

  function initializeAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initializeSection(context);
    toArray(context.querySelectorAll ? context.querySelectorAll(ROOT_SELECTOR) : []).forEach(initializeSection);
  }

  window.AykahPdpSocialShowcase = window.AykahPdpSocialShowcase || { initializeAll: initializeAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(document); }, { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
